import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Building2, Users, Plus, Search, Edit, Trash2, 
  CheckCircle, Phone, MapPin, RefreshCw, Eye, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Platform Stats Card
const PlatformStatsCard = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Total Organizations</p>
              <p className="text-2xl font-bold text-white">{stats.total_organizations || 0}</p>
            </div>
            <Building2 className="h-8 w-8 text-blue-400" />
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Total Users</p>
              <p className="text-2xl font-bold text-white">{stats.total_users || 0}</p>
            </div>
            <Users className="h-8 w-8 text-purple-400" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Create Organization Dialog
const CreateOrganizationDialog = ({ open, onOpenChange, onSuccess, token }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    enable_crm: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/organizations?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Organization created successfully');
        onSuccess();
        onOpenChange(false);
        setFormData({ name: '', address: '', phone: '', enable_crm: false });
      } else {
        toast.error(data.error || data.detail || 'Failed to create organization');
      }
    } catch (error) {
      toast.error('Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="create-org-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-500" />
            Create Organization
          </DialogTitle>
          <DialogDescription>
            Add a new organization to the system
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="org-name">Organization Name *</Label>
            <Input
              id="org-name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Acme Corporation"
              required
              data-testid="org-name-input"
            />
          </div>

          <div>
            <Label htmlFor="org-address">Address</Label>
            <Input
              id="org-address"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="123 Business St, City, Country"
              data-testid="org-address-input"
            />
          </div>

          <div>
            <Label htmlFor="org-phone">Phone</Label>
            <Input
              id="org-phone"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="+1 (555) 000-0000"
              data-testid="org-phone-input"
            />
          </div>

          <div className="flex items-center space-x-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
            <input
              type="checkbox"
              id="enable-crm"
              checked={formData.enable_crm}
              onChange={(e) => setFormData({...formData, enable_crm: e.target.checked})}
              className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
              data-testid="enable-crm-checkbox"
            />
            <div>
              <Label htmlFor="enable-crm" className="text-sm font-medium cursor-pointer">
                Enable CRM Module
              </Label>
              <p className="text-xs text-slate-400">
                Allows access to Contacts, Leads, Deals, and Pipeline features
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? 'Creating...' : 'Create Organization'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edit Organization Dialog
const EditOrganizationDialog = ({ open, onOpenChange, organization, onSuccess, token }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: ''
  });

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        address: organization.address || '',
        phone: organization.phone || ''
      });
    }
  }, [organization]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!organization) return;
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/organizations/${organization.id}?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Organization updated successfully');
        onSuccess();
        onOpenChange(false);
      } else {
        toast.error(data.error || data.detail || 'Failed to update organization');
      }
    } catch (error) {
      toast.error('Failed to update organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-org-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-amber-500" />
            Edit Organization
          </DialogTitle>
          <DialogDescription>
            Update organization details
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="edit-org-name">Organization Name *</Label>
            <Input
              id="edit-org-name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Acme Corporation"
              required
              data-testid="edit-org-name-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-org-address">Address</Label>
            <Input
              id="edit-org-address"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="123 Business St, City, Country"
              data-testid="edit-org-address-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-org-phone">Phone</Label>
            <Input
              id="edit-org-phone"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="+1 (555) 000-0000"
              data-testid="edit-org-phone-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700">
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const OrganizationManagement = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/organizations?token=${token}`);
      const data = await response.json();
      
      if (data.success) {
        setOrganizations(data.organizations || []);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/organizations/stats?token=${token}`);
      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchOrganizations();
    fetchStats();
  }, [token]);

  const handleDeleteOrganization = async (orgId) => {
    if (!window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/organizations/${orgId}?token=${token}`, {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Organization deleted successfully');
        fetchOrganizations();
        fetchStats();
      } else {
        toast.error(data.error || data.detail || 'Failed to delete organization');
      }
    } catch (error) {
      toast.error('Failed to delete organization');
    }
  };

  const handleEditClick = (org) => {
    setSelectedOrg(org);
    setShowEditDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      timeZone: 'Asia/Dubai',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredOrganizations = organizations.filter(org =>
    org.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.phone?.includes(searchQuery)
  );

  return (
    <div className="p-6" data-testid="organization-management-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="h-7 w-7 text-blue-500" />
            Organizations
          </h1>
          <p className="text-slate-500 mt-1">Manage all organizations in the system</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => { fetchOrganizations(); fetchStats(); }}
            className="gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/organizations/modules')}
            className="gap-2"
            data-testid="manage-modules-btn"
          >
            <Settings size={16} />
            Modules & Integrations
          </Button>
          <Button 
            onClick={() => setShowCreateDialog(true)} 
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            data-testid="create-org-btn"
          >
            <Plus size={16} />
            Create Organization
          </Button>
        </div>
      </div>

      {/* Stats */}
      <PlatformStatsCard stats={stats} />

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search organizations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="org-search-input"
          />
        </div>
      </div>

      {/* Organizations Table */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle className="text-lg">All Organizations</CardTitle>
          <CardDescription>{filteredOrganizations.length} organization(s)</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">ORGANIZATION</TableHead>
                <TableHead className="font-semibold">ADDRESS</TableHead>
                <TableHead className="font-semibold">PHONE</TableHead>
                <TableHead className="font-semibold">USERS</TableHead>
                <TableHead className="font-semibold">CREATED</TableHead>
                <TableHead className="font-semibold text-right">ACTIONS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    Loading organizations...
                  </TableCell>
                </TableRow>
              ) : filteredOrganizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    {searchQuery ? 'No organizations match your search' : 'No organizations found. Create one to get started.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrganizations.map((org) => (
                  <TableRow key={org.id} data-testid={`org-row-${org.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-semibold">
                          {org.name?.[0]?.toUpperCase() || 'O'}
                        </div>
                        <span className="font-medium text-slate-900">{org.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {org.address ? (
                        <div className="flex items-center gap-1">
                          <MapPin size={14} className="text-slate-400" />
                          <span className="truncate max-w-[200px]">{org.address}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {org.phone ? (
                        <div className="flex items-center gap-1">
                          <Phone size={14} className="text-slate-400" />
                          {org.phone}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="gap-1">
                        <Users size={12} />
                        {org.user_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDate(org.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/organizations/${org.id}`)}
                          className="h-8 w-8 p-0 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                          title="View Dashboard"
                          data-testid={`view-org-${org.id}`}
                        >
                          <Eye size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(org)}
                          className="h-8 w-8 p-0 text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                          title="Edit Organization"
                          data-testid={`edit-org-${org.id}`}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteOrganization(org.id)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                          title="Delete Organization"
                          data-testid={`delete-org-${org.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Organization Dialog */}
      <CreateOrganizationDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={() => { fetchOrganizations(); fetchStats(); }}
        token={token}
      />

      {/* Edit Organization Dialog */}
      <EditOrganizationDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        organization={selectedOrg}
        onSuccess={() => { fetchOrganizations(); fetchStats(); }}
        token={token}
      />
    </div>
  );
};

export default OrganizationManagement;
